// ── sync-repair ──────────────────────────────────────────────────────────────
// Decides, from a submission's own stored payload, whether its dashboard
// numbers ever landed — and whether the app may rebuild them by itself.
//
// Why this exists: a Production Report / Weekly Timesheet is written twice.
// The submission row + PDF land first; the per-day money rows (production_
// reports / time_entries) land second, through an RPC. When that second write
// failed — stale schema cache, a constraint, a dropped connection — the app
// used to console.warn and move on: a filed form, a clean PDF, and a P&L that
// never moved. It took a support session to notice and repair by hand, twice
// in one day (Langley, 2026-08-16).
//
// The payload the submission already carries is sufficient to rebuild both
// forms (proved by doing exactly that by hand, twice). This module is the
// pure-decision half; app.js runs the scan and offline.js replays the repair
// through the SAME sync functions the online path uses.

// What the week SHOULD contain, computed from the form payload — not from
// what happened to reach the database.
export function expectedProductionUnits(data) {
  const items = data?.production?.items;
  if (!Array.isArray(items)) return 0;
  let total = 0;
  for (const item of items) {
    const qty = item?.qty || {};
    for (const day of Object.keys(qty)) {
      const q = parseFloat(qty[day]);
      if (q > 0) total += q;
    }
  }
  return total;
}

export function expectedTimesheetHours(data) {
  const rows = data?.timesheet;
  if (!Array.isArray(rows)) return 0;
  let total = 0;
  for (const w of rows) {
    const days = w?.days || {};
    for (const day of Object.keys(days)) {
      const r  = parseFloat(days[day]?.r);
      const ot = parseFloat(days[day]?.ot);
      if (r > 0)  total += r;
      if (ot > 0) total += ot;
    }
  }
  return total;
}

// Sick cells carry no r/ot — the toggle clears them — but a PAID sick day is
// still written to time_entries as hours, and it is still labour cost on the
// P&L. Counting hours alone made a week of nothing but paid sick leave look
// like an empty week, so the one case where a missed sync is invisible on both
// the PDF and the dashboard was the one case the watchdog refused to repair.
//
// UNPAID sick days (over the 5/year allowance, flagged by the submit path) are
// deliberately NOT counted: the sync writes them to sick_days and returns
// before time_entries, so zero rows there is the CORRECT outcome. Counting
// them made a week of nothing but unpaid absence look broken forever — the
// rebuild was a no-op, attempts ran out, and the admin was told to re-file
// (same result) or delete (which cascades the sick_days rows away).
export function expectedSickDays(data) {
  const rows = data?.timesheet;
  if (!Array.isArray(rows)) return 0;
  let n = 0;
  for (const w of rows) {
    const days = w?.days || {};
    for (const day of Object.keys(days)) {
      const cell = days[day];
      if (cell?.sick === true && cell?.sick_unpaid !== true) n++;
    }
  }
  return n;
}

// A sick_days row the database stamped (migration 20261028100000): the worker
// has hours on another sheet that day, so the sheet's paid sick day was left
// out of time_entries on purpose — like an unpaid day above, zero rows for it
// is the correct outcome.
export const isUnpaidElsewhere = row => /\(UNPAID — hours on another sheet\)/.test(String((row && row.note) || ''));

// A submission needs repair when its payload says the week held work and the
// database says nothing arrived. A deliberately emptied week (a correction
// that stripped every line) expects zero — repairing it would be a no-op, so
// it is left alone rather than re-queued forever. unpaidElsewhere = how many
// of the sheet's sick_days rows are stamped isUnpaidElsewhere.
export function needsRepair(submissionType, data, structuredRowCount, unpaidElsewhere = 0) {
  if (structuredRowCount > 0) return false;
  if (submissionType === 'Production Report')
    return expectedProductionUnits(data) > 0;
  if (submissionType === 'Weekly Timesheet')
    return expectedTimesheetHours(data) > 0 || expectedSickDays(data) - unpaidElsewhere > 0;
  return false;
}

// May the app rebuild this one silently, or must a person look at it?
//
//   'queue'  — rebuild automatically under the author's own identity.
//   'notify' — tell the admin instead. Four cases earn this:
//              · a production report AUTHORED BY AN ADMIN in an org that has
//                subcontractor crews. The submission row does not record which
//                crew the units belonged to (only the live submit and the
//                queued record know), so an automatic rebuild could file
//                another crew's production under the admin — and Job Costs
//                prices sub labour off exactly that column.
//              · the week is already covered by ANOTHER filing. A submission
//                the person deliberately REPLACED looks identical to a broken
//                one — payload with work, zero rows — and rebuilding it would
//                silently double the week. Ambiguous money is a person's call.
//              · the rebuild has already been tried and changed nothing. Some
//                payloads can never write a row (every material renamed, every
//                worker gone); without this the watchdog re-queued them every
//                five minutes forever, toasting each time.
//   'skip'   — already queued for repair; do not double it.
export const MAX_REPAIR_ATTEMPTS = 2;

export function repairDecision({
  submissionType, alreadyQueued, authorIsAdmin, orgHasSubcontractors,
  weekCoveredByAnother = false, attempts = 0,
}) {
  if (alreadyQueued) return 'skip';
  if (attempts >= MAX_REPAIR_ATTEMPTS) return 'notify';
  if (weekCoveredByAnother) return 'notify';
  if (submissionType === 'Production Report' && authorIsAdmin && orgHasSubcontractors)
    return 'notify';
  return 'queue';
}
